import React, { useState } from 'react';
import axios from 'axios';
import { FaUser, FaPhone, FaMapMarkerAlt, FaCity, FaEnvelope, FaArrowRight, FaTimes, FaSpinner } from 'react-icons/fa';
import '../styles/ShippingForm.css';

const ShippingForm = ({ pack, user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'España'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [addressValid, setAddressValid] = useState(null); // null = sin validar, true = válida, false = inválida

  // Validar formato de teléfono español
  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\.]/g, '');
    return /^(\+34)?[679]\d{8}$/.test(cleaned);
  };

  // Validar formato de código postal español
  const isValidPostalCode = (code) => {
    return /^\d{5}$/.test(code.trim());
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Resetear validación al cambiar datos
    setAddressValid(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name.trim()) {
      setError('Nombre completo es obligatorio');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Teléfono es obligatorio');
      return;
    }
    if (!isValidPhone(formData.phone)) {
      setError('Formato de teléfono inválido. Debe ser un número español (ej: 612345678)');
      return;
    }
    if (!formData.address.trim()) {
      setError('Dirección es obligatoria');
      return;
    }
    if (!formData.city.trim()) {
      setError('Ciudad es obligatoria');
      return;
    }
    if (!formData.postal_code.trim()) {
      setError('Código postal es obligatorio');
      return;
    }
    if (!isValidPostalCode(formData.postal_code)) {
      setError('El código postal debe tener 5 dígitos (ej: 28004)');
      return;
    }
    
    setLoading(true);
    setError('');
    setValidating(true);
    setAddressValid(null);
    
    try {
      // 1. Validar dirección contra la API real
      const validationResponse = await axios.post(
        'https://reanimeitor.com/backend/validate_address.php',
        {
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country
        },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      
      if (!validationResponse.data.success || !validationResponse.data.valid) {
        setAddressValid(false);
        setError(validationResponse.data.message || 'La combinación de ciudad, código postal y país no es válida. Verifica los datos.');
        setLoading(false);
        setValidating(false);
        return;
      }
      
      setAddressValid(true);
      
      // 2. Guardar dirección
      const response = await axios.post(
        'https://reanimeitor.com/backend/save_shipping_address.php',
        {
          user_id: user.id,
          ...formData
        },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      
      if (response.data.success) {
        onSuccess(formData);
      } else {
        setError(response.data.error || 'Error al guardar dirección');
      }
    } catch (err) {
      console.error(err);
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  return (
    <div className="shipping-modal-overlay">
      <div className="shipping-modal">
        <button className="shipping-modal-close" onClick={onCancel}>
          <FaTimes />
        </button>
        <div className="shipping-modal-header">
          <h2>Información de Envío</h2>
          <p>Completa tus datos para recibir tu reanimeitor Genesis Can</p>
        </div>
        
        <form onSubmit={handleSubmit} className="shipping-form">
          <div className="form-group">
            <label><FaUser /> Nombre completo</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Nombre y apellidos"
              required
            />
          </div>
          
          <div className="form-group">
            <label><FaPhone /> Teléfono</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Teléfono de contacto (ej: 612345678)"
              required
            />
          </div>
          
          <div className="form-group">
            <label><FaMapMarkerAlt /> Dirección</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Calle, número, piso"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label><FaCity /> Ciudad</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Ciudad"
                required
              />
            </div>
            
            <div className="form-group">
              <label><FaEnvelope /> Código Postal</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="Código postal (5 dígitos)"
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>País</label>
            <select name="country" value={formData.country} onChange={handleChange}>
              <option value="España">España</option>
              <option value="Andorra">Andorra</option>
              <option value="Francia">Francia</option>
              <option value="Portugal">Portugal</option>
              <option value="Alemania">Alemania</option>
              <option value="Italia">Italia</option>
            </select>
          </div>
          
          {validating && (
            <div className="shipping-validating">
              <FaSpinner className="spinning-icon" /> Verificando dirección...
            </div>
          )}
          {addressValid === true && !error && (
            <div className="shipping-valid">
              ✅ Dirección verificada correctamente
            </div>
          )}
          {error && <div className="shipping-error">{error}</div>}
          
          <div className="shipping-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-submit" disabled={loading || validating}>
              {loading || validating ? 'Validando...' : (
                <>
                  Continuar al pago <FaArrowRight />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingForm;